import './GeneralTab.css';
import React, { useState } from 'react';
import { Alert, Button, Col, Row, Typography, theme } from 'antd';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import type { Dayjs } from 'dayjs';
import type { OverviewStats } from '../../../../../domain/use_cases/leedflow/overview/getOverviewStats';
import type { LiveStats } from '../../../../../domain/use_cases/leedflow/overview/getLiveStats';
import LiveBar from '../LiveBar';
import KpiRow from '../KpiRow';
import ActivityChart from '../ActivityChart';
import TopSubscriptionsPanel from '../TopSubscriptionsPanel';
import BreakdownPanel from '../BreakdownPanel';
import LastCallsTable from '../LastCallsTable';
import LastSmsTable from '../LastSmsTable';
import UsersMapPanel from '../UsersMapPanel';

const { useToken } = theme;

// Traduit les clés techniques de `stats.partial[].block` (backend/src/helpers/leedflow/overview.helpers.ts)
// en libellés lisibles pour l'alerte de dégradation partielle.
const PARTIAL_BLOCK_LABELS: Record<string, string> = {
  calls: 'appels',
  sms: 'SMS',
  voicemails: 'messagerie vocale',
  costs: 'coûts',
  previous: 'comparaison période précédente',
  lastCalls: 'derniers appels',
  devices: 'appareils',
};

const partialLabel = (key: string): string => PARTIAL_BLOCK_LABELS[key] ?? key;

interface GeneralTabProps {
  stats: OverviewStats;
  /** Période et filtre de la page — la table des appels pagine dessus. */
  period?: [Dayjs | null, Dayjs | null];
  subscriptionIds?: string[];
  exclude?: string[];
  /** Ajoute un abonnement au filtre de la page depuis la carte. */
  onSelectSubscription?: (subscriptionId: string) => void;
  live: LiveStats;
  liveStatus: ContextStatus;
  status: ContextStatus;
  /** Masque uniquement la tuile de coût de `KpiRow`. */
  isConfidential?: boolean;
}

// Ordre de lecture : le temps réel, la courbe d'activité, puis les totaux et
// la carte côte à côte, la répartition, le classement, et enfin les deux
// tables de détail. Du « que se passe-t-il » vers le « qui » puis le « quoi
// exactement ».
//
// Il diffère de celui du brief initial (Task 15), qui ouvrait sur les tuiles :
// la courbe est remontée sous le temps réel, les deux répondant à la même
// question à deux échelles de temps.
const GeneralTab: React.FC<GeneralTabProps> = ({
  stats,
  period,
  subscriptionIds,
  exclude,
  status,
  live,
  liveStatus,
  isConfidential = false,
  onSelectSubscription,
}) => {
  const { token } = useToken();
  const [showPartialDetails, setShowPartialDetails] = useState(false);
  if (status === ContextStatus.ERROR) {
    return (
      <Alert
        type="error"
        showIcon
        message="Impossible de charger les données"
        description="Les statistiques n'ont pas pu être récupérées. Réessayez ou changez la période."
      />
    );
  }

  return (
    <>
      {stats.partial && stats.partial.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: token.marginLG }}
          message={`Données incomplètes : ${stats.partial.map((p) => partialLabel(p.block)).join(', ')} — ${stats.partial.length > 1 ? 'ces blocs sont partiels' : 'ce bloc est partiel'}.`}
          action={
            <Button size="small" type="link" onClick={() => setShowPartialDetails(!showPartialDetails)}>
              {showPartialDetails ? 'Voir moins' : 'Voir plus'}
            </Button>
          }
          description={
            showPartialDetails ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                {stats.partial.map((entry) => (
                  <div key={entry.block}>
                    <Typography.Text strong>{partialLabel(entry.block)}</Typography.Text>
                    {/* Motif brut renvoyé par Firestore, non reformulé : c'est
                        lui qui porte le lien de création d'index ou le nom du
                        champ fautif. Le reformuler perdrait ce qui sert. */}
                    <Typography.Paragraph
                      copyable
                      style={{ marginBottom: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}
                    >
                      {entry.message}
                    </Typography.Paragraph>
                  </div>
                ))}
              </div>
            ) : null
          }
        />
      )}

      <div style={{ marginBottom: token.marginLG }}>
        <LiveBar stats={live} status={liveStatus} />
      </div>

      {/* La courbe d'activité juste sous le temps réel : les deux répondent à
          la même question, « que se passe-t-il », l'une à l'instant et l'autre
          sur la période. Les totaux et la carte viennent après. */}
      <div style={{ marginBottom: token.marginLG }}>
        <ActivityChart buckets={stats.buckets} status={status} />
      </div>

      {/* Deux moitiés : les chiffres à gauche, la carte à droite. `align="stretch"`
          pour que la carte prenne toute la hauteur de la pile de tuiles plutôt
          que de laisser un blanc sous elle. */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={13} className="overview-general__kpi">
          <KpiRow stats={stats} previous={stats.previous} status={status} isConfidential={isConfidential} />
        </Col>
        <Col xs={24} lg={11}>
          <UsersMapPanel
            devices={stats.deviceBreakdown}
            points={stats.mapPoints}
            status={status}
            onSelectSubscription={onSelectSubscription}
          />
        </Col>
      </Row>

      {/* La Répartition porte trois donuts côte à côte : elle prend toute la
          largeur, le classement des abonnements passe en dessous. */}
      <div style={{ marginTop: token.marginLG }}>
        <BreakdownPanel breakdown={stats.callsStats.breakdown} devices={stats.deviceBreakdown} status={status} />
      </div>

      <div style={{ marginTop: token.marginLG }}>
        <TopSubscriptionsPanel rows={stats.topSubscriptions} status={status} />
      </div>

      {/* Les totaux viennent des agrégats, pas de la longueur des tables :
          celles-ci ne portent que les plus récents. `handledCalls` exclut les
          appels en défaut, exactement comme la sélection de `buildLastCalls`
          — c'est aussi ce qui borne la pagination serveur de la table. */}
      <LastCallsTable
        rows={stats.lastCalls}
        total={stats.callsStats.handledCalls}
        status={status}
        period={period}
        subscriptionIds={subscriptionIds}
        exclude={exclude}
      />

      <LastSmsTable
        rows={stats.lastSms ?? []}
        total={stats.smsStats.sentCount + stats.smsStats.receivedCount}
        status={status}
        period={period}
        subscriptionIds={subscriptionIds}
        exclude={exclude}
      />
    </>
  );
};

export default GeneralTab;
